import { RequestRoot } from 'types';
import { FeatureArray } from '../model/features';

export class RequestViewTemplate {
  elements: ViewTemplateElement[];
}

export class ViewTemplateElement {
  get extractor(): (_: RequestRoot) => any {
    return this._extractor;
  }
  get row(): number {
    return this._row;
  }
  get col(): number {
    return this._col;
  }
  get rowSpan(): number {
    return this._rowSpan;
  }
  get colSpan(): number {
    return this._colSpan;
  }
  get rightCol(): number {
    return this._col + this._colSpan;
  }
  get downRow(): number {
    return this._rowSpan + this._row;
  }
  get style(): string {
    let res =
      'background: ' +
      this._background +
      '; color: ' +
      this._foreground +
      '; text-align: ' +
      this._align +
      '; vertical-align: ' +
      this._verticalAlign +
      ';';
    if (this._border) {
      res += 'border: solid; border-radius: 15px; border-color: ' + this._borderColor + ';';
    }
    return res;
  }
  get classes(): string[] {
    return this._classes;
  }
  private constructor() {}
  private readonly BOLD = 'bold';
  private readonly UNDERLINE = 'underline';
  private readonly ITALIC = 'italic';
  private _extractor: (_: RequestRoot) => any;
  private _row: number;
  private _col: number;
  private _rowSpan = 1;
  private _colSpan = 1;
  private _classes: string[] = [];
  private _background = 'white';
  private _foreground = 'black';
  private _border = false;
  private _borderColor = 'black';
  private _align: 'center' | 'left' | 'right' = 'center';
  private _verticalAlign: 'middle' | 'top' | 'bottom' = 'middle';
  static EmptyElement(col: number, row: number): ViewTemplateElement {
    const res = new ViewTemplateElement();
    res._col = col;
    res._row = row;
    res._colSpan = 1;
    res._rowSpan = 1;
    res._classes = [];
    res._extractor = () => '';
    return res;
  }
  static TextElement(col: number, row: number, val: string): ViewTemplateElement {
    const res = this.EmptyElement(col, row);
    res._extractor = (_) => {
      return '<span>' + val + '</span>';
    };
    return res;
  }
  static PathElement(col: number, row: number, path: (_: RequestRoot) => string): ViewTemplateElement {
    const res = this.EmptyElement(col, row);
    res._extractor = (_) => {
      return '<span>' + path(_) + '</span>';
    };
    return res;
  }
  static RequestImageElement(col: number, row: number, tag: string) {
    const res = this.EmptyElement(col, row);
    res._extractor = (_: RequestRoot) => {
      return '<img width="100%" src="' + _.readBlobURL(tag) + '"</img>';
    };
    return res;
  }
  static ResourceImageElement(col: number, row: number, resourceKey: string) {
    const res = this.EmptyElement(col, row);
    res._extractor = (_) => {
      const src = 'assets/images/' + resourceKey;
      return '<img width="100%" src="' + src + '"</img>';
    };
    return res;
  }
  static SubRequestViewElement(col: number, row: number, subTemplateKey: string) {
    const res = this.EmptyElement(col, row);
    res._extractor = (_) => {
      return '<!-- <i-request-view templatekey="' + subTemplateKey + '"></i-request-view> -->';
    };
    return res;
  }
  static TableRequestViewElement<T>(
    col: number,
    row: number,
    columns: [string, (_: T) => any][],
    data: (_: RequestRoot) => FeatureArray<T>,
    transposed: boolean = false,
  ) {
    const res = this.EmptyElement(col, row);
    res._extractor = transposed
      ? (_: RequestRoot) => {
          const elementValue: FeatureArray<T> = data(_);
          let html = '<table style="width: 100%;">';
          columns.forEach((c) => {
            html += '<tr>';
            html += '<th>' + c[0] + '</th>';
            elementValue.forEach((item: T) => {
              html += '<td>' + c[1](item) + '</td>';
            });
            html += '<tr>';
          });
          html += '</table>';
          return html;
        }
      : (_) => {
          const elementValue: FeatureArray<T> = data(_);
          let html = '<table style="width: 100%;"><tr>';
          columns.forEach((c) => {
            html += '<th>' + c[0] + '</th>';
          });
          html += '</tr>';
          elementValue.forEach((item: T) => {
            html += '<tr>';
            columns.forEach((c) => {
              html += '<td>' + c[1](item) + '</td>';
            });
            html += '</tr>';
          });
          html += '</table>';
          return html;
        };
    return res;
  }
  private clone(): ViewTemplateElement {
    const res = ViewTemplateElement.EmptyElement(this._col, this._row);
    res._colSpan = this._colSpan;
    res._rowSpan = this._rowSpan;
    res._extractor = this._extractor;
    res._classes = this._classes.map((_) => _);
    res._background = this._background;
    res._foreground = this._foreground;
    res._border = this._border;
    res._borderColor = this._borderColor;
    res._align = this._align;
    res._verticalAlign = this._verticalAlign;
    return res;
  }
  setColSpan(val: number): ViewTemplateElement {
    const res = this.clone();
    res._colSpan = val;
    return res;
  }
  setRowSpan(val: number): ViewTemplateElement {
    const res = this.clone();
    res._rowSpan = val;
    return res;
  }
  setBold(val: boolean): ViewTemplateElement {
    return this.setClass(this.BOLD, val);
  }
  setItalic(val: boolean): ViewTemplateElement {
    return this.setClass(this.ITALIC, val);
  }
  setUnderline(val: boolean): ViewTemplateElement {
    return this.setClass(this.UNDERLINE, val);
  }
  private setClass(className: string, val: boolean) {
    const res = this.clone();
    res._classes = res._classes.filter((_) => _ !== className);
    if (val) {
      res._classes.push(className);
    }
    return res;
  }
  setBackground(background: string): ViewTemplateElement {
    const res = this.clone();
    res._background = background;
    return res;
  }
  setForeground(foreground: string): ViewTemplateElement {
    const res = this.clone();
    res._foreground = foreground;
    return res;
  }
  setBorder(val: boolean): ViewTemplateElement {
    const res = this.clone();
    res._border = val;
    return res;
  }
  setBorderColor(val: string): ViewTemplateElement {
    const res = this.clone();
    res._borderColor = val;
    return res;
  }
  setAlign(val: 'center' | 'left' | 'right'): ViewTemplateElement {
    const res = this.clone();
    res._align = val;
    return res;
  }
  setVerticalAlign(val: 'middle' | 'bottom' | 'top'): ViewTemplateElement {
    const res = this.clone();
    res._verticalAlign = val;
    return res;
  }
}

export class RequestViewTemplatesRegistry {
  private static templates: Map<string, RequestViewTemplate> = new Map<string, RequestViewTemplate>();

  static addTemplate(key: string, template: RequestViewTemplate): void {
    RequestViewTemplatesRegistry.templates.set(key, template);
  }
  static getTemplate(key: string): RequestViewTemplate | undefined {
    return RequestViewTemplatesRegistry.templates.get(key);
  }
}
